import { useToast } from '@plaidev/sour-react';
import type { GlobalNaviGrantActions } from '@rt/global-navi/react';
import { Domain } from 'auth-common/domain';
import { Env } from 'auth-common/shared';
import { useCallback, useState } from 'react';
import { useAuth } from '../Api/Auth';

import AccountGrant = Domain.Object.Account.Grant;

const getRequestGrantToastTitle = (lifecycleState: AccountGrant.LifecycleState, grantKey: 'systemAdmin' | 'piiAccess'): string => {
  const key = grantKey === 'systemAdmin' ? AccountGrant.Key.SystemAdminActive : AccountGrant.Key.PiiAccessActive;
  if (lifecycleState === AccountGrant.LifecycleState.Requested) {
    return key === AccountGrant.Key.SystemAdminActive
      ? 'システム管理者権限の承認をリクエストしました'
      : '個人情報アクセスの承認をリクエストしました';
  }
  return key === AccountGrant.Key.SystemAdminActive ? 'システム管理者権限を有効化しました' : '個人情報アクセスを有効化しました';
};

const getGrantRequestInactiveToastTitle = (grantKey: 'systemAdmin' | 'piiAccess'): string =>
  grantKey === 'systemAdmin'
    ? 'システム管理者権限のリクエストは承認されませんでした'
    : '個人情報アクセスのリクエストは承認されませんでした';

const toEphemeralKey = (grantKey: 'systemAdmin' | 'piiAccess'): AccountGrant.Key.Ephemeral =>
  grantKey === 'systemAdmin' ? AccountGrant.Key.SystemAdminActive : AccountGrant.Key.PiiAccessActive;

const toPendingKey = (grantKey: AccountGrant.Key.Ephemeral): 'systemAdmin' | 'piiAccess' =>
  grantKey === AccountGrant.Key.SystemAdminActive ? 'systemAdmin' : 'piiAccess';

const getPamConsoleUrl = (): string =>
  `https://console.cloud.google.com/iam-admin/pam/grants/approvals?project=${encodeURIComponent(Env.Var.FirebaseAuth.ProjectId)}`;

/**
 * GlobalNavi の grant メニュー mutation / ポーリング / ダイアログ用 hook。
 * eligibility / active 状態は `GlobalNaviDataV2.user.grants` が担うため prop には含めない。
 */
export const useGlobalNaviGrantActions = (params: { isEmployee: boolean }): GlobalNaviGrantActions => {
  const toast = useToast();
  const [pendingGrantRequest, setPendingGrantRequest] = useState<{ grantKey: AccountGrant.Key.Ephemeral } | undefined>();

  const { requestActiveGrant, isRequestingActiveGrant, requestingActiveGrantKey } = useAuth.RequestActiveGrant({
    onSuccess: (grant, variables) => {
      const grantKey = toPendingKey(variables.grantKey);
      const toastTitle = getRequestGrantToastTitle(grant.lifecycleState, grantKey);
      if (grant.lifecycleState === AccountGrant.LifecycleState.Requested) {
        toast.info(
          toastTitle,
          params.isEmployee
            ? {
                label: 'PAM を開く',
                duration: 15000,
                action: () => window.open(getPamConsoleUrl(), '_blank', 'noopener,noreferrer'),
              }
            : undefined,
        );
      } else {
        toast.success(toastTitle);
      }
      setPendingGrantRequest(grant.lifecycleState === AccountGrant.LifecycleState.Requested ? { grantKey: variables.grantKey } : undefined);
    },
  });

  const { revokeActiveGrant, isRevokingActiveGrant, revokingActiveGrantKey } = useAuth.RevokeActiveGrant({
    onSuccess: (_, variables) => {
      toast.success(
        variables.grantKey === AccountGrant.Key.SystemAdminActive
          ? 'システム管理者権限を無効化しました'
          : '個人情報アクセスを無効化しました',
      );
    },
  });

  const handleActiveGrantRequestActivated = useCallback(
    (grant: { grantKey: AccountGrant.Key.Ephemeral; lifecycleState: typeof AccountGrant.LifecycleState.Active }) => {
      toast.success(getRequestGrantToastTitle(grant.lifecycleState, toPendingKey(grant.grantKey)));
      setPendingGrantRequest(undefined);
    },
    [toast],
  );

  const handleActiveGrantRequestInactive = useCallback(
    (grant: { grantKey: AccountGrant.Key.Ephemeral }) => {
      toast.error(getGrantRequestInactiveToastTitle(toPendingKey(grant.grantKey)));
      setPendingGrantRequest(undefined);
    },
    [toast],
  );

  useAuth.PollActiveGrantRequestStatus({
    grantKey: pendingGrantRequest?.grantKey,
    onActive: handleActiveGrantRequestActivated,
    onInactive: handleActiveGrantRequestInactive,
  });

  const isMutating = isRequestingActiveGrant || isRevokingActiveGrant;
  const mutatingEphemeralKey = requestingActiveGrantKey ?? revokingActiveGrantKey;

  return {
    pendingGrantKey: pendingGrantRequest ? toPendingKey(pendingGrantRequest.grantKey) : undefined,
    mutatingGrantKey: mutatingEphemeralKey ? toPendingKey(mutatingEphemeralKey) : undefined,
    isMutating,
    onRequest: ({ grantKey, reason, clientConsent }) => {
      const ephemeralKey = toEphemeralKey(grantKey);
      requestActiveGrant({
        grantKey: ephemeralKey,
        ...(reason ? { reason } : {}),
        ...(clientConsent ? { clientConsent } : {}),
      });
    },
    onRevoke: ({ grantKey }) => {
      const ephemeralKey = toEphemeralKey(grantKey);
      revokeActiveGrant({ grantKey: ephemeralKey });
    },
  };
};
